import { Component, computed, inject, input } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_CARD, KENDO_GRIDLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import { AgenciesClient, AgencyDetailedDto, AgencyUpdateDto, TransitionRequestDto } from '@unity/clients';
import { GridAgencyRoleSharedComponent } from '@unity/components/shared/grid-agency-role-shared/grid-agency-role-shared.component';
import { GridAgencyUserSharedComponent } from '@unity/components/shared/grid-agency-user-shared/grid-agency-user-shared.component';
import { MultiselectCommunityControlComponent } from '@unity/forms/multiselect-community-control/multiselect-community-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';

import { rxResource } from '@angular/core/rxjs-interop';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { FormLayoutComponent } from '@unity/layouts/form-layout/form-layout.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { emailFormControl, phoneFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY, forkJoin } from 'rxjs';

class AgencyUpdateSchema implements FormGroupControlSchema<AgencyUpdateDto> {
  public name: FormControl<string>;
  public contact: FormControl<string>;
  public phone: FormControl<string>;
  public email: FormControl<string>;
  public community_ids: FormControl<number[]>;
  public code: FormControl<string>;

  public constructor(agency?: AgencyDetailedDto) {
    this.name = stringFormControl({ disabled: true, value: agency?.name });
    this.contact = stringFormControl({ disabled: true, value: agency?.contact });
    this.phone = phoneFormControl({ disabled: true, value: agency?.phone });
    this.email = emailFormControl({ disabled: true, value: agency?.email });
    this.community_ids = new FormControl<number[]>(
      { disabled: true, value: agency?.communities?.map(c => c.id) ?? [] },
      { nonNullable: true }
    );

    this.code = stringFormControl({ disabled: true, value: agency?.code });
  }
}

@Component({
  selector: 'unity-manage-agency-page',
  standalone: true,
  imports: [
    KENDO_CARD,
    KENDO_GRIDLAYOUT,
    ReactiveFormsModule,
    PageContainerComponent,
    TextboxControlComponent,
    MultiselectCommunityControlComponent,
    GridAgencyRoleSharedComponent,
    GridAgencyUserSharedComponent,
    FormLayoutComponent,
    GridAgencyRoleSharedComponent,
    PanelManageSharedComponent,
    AuthorizeDirective,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './manage-agency-page.component.html',
  styleUrl: './manage-agency-page.component.scss',
})
export class ManageAgencyPageComponent extends AbstractManageComponent<AgencyDetailedDto, AgencyUpdateDto, AgencyUpdateSchema> {
  private readonly agenciesClient = inject(AgenciesClient);

  public readonly $agencyId = input.required({ alias: 'agencyId', transform: (value: string) => parseInt(value) });
  public readonly $myBusiness = input.required<boolean>({ alias: 'myBusiness' });

  protected override editEntitlement: UnityEntitlementPath = 'Agency:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'Agency:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'Agency:Delete';
  protected override resourceName = 'Business Partner';
  protected override $resourceId = this.$agencyId;

  protected override readonly $contentHeader = computed(() => {
    const agency = this.$resource();
    const myBusiness = this.$myBusiness();

    if (myBusiness) {
      return `My Business Management: ${agency?.name}`;
    }
    return `Business Partner: ${agency?.name}`;
  });

  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      return resourceId ? { resourceId } : undefined;
    },
    stream: ({ params: { resourceId } }) => this.agenciesClient.getOneAsDetailed(resourceId),
  });

  protected override availableTransitionsFn$ = (agencyId: number) => this.availableTransitionsClient.getAllForAgencyAsMini(agencyId);

  protected override updateResourceFn$ = (agencyId: number, form: FormGroup<AgencyUpdateSchema>) =>
    forkJoin({
      updateAgencyCommunities: this.agenciesClient.updateCommunitiesInOne(agencyId, form.getRawValue().community_ids),
      updateAgency: this.agenciesClient.updateOne(agencyId, form.getRawValue()),
    });
  protected override deleteResourceFn$ = (_: number) => EMPTY;

  protected override doTransitionFn$ = (resourceId: number, transition: TransitionRequestDto) =>
    this.transitionsClient.setForAgency(resourceId, transition);

  protected override newControlsFn(dto?: AgencyDetailedDto) {
    return new AgencyUpdateSchema(dto);
  }

  protected readonly $createUserAction = computed(() => {
    const resource = this.$resource();
    if (!resource) {
      return;
    }

    if (resource.is_internal) {
      return QuickActions.CreateInternalUser(this.$resourceId());
    }
    return QuickActions.CreateBusinessPartnerUser(this.$resourceId());
  });

  protected readonly $returnToDashboardAction = computed(() => QuickActions.ReturnToDashboard);
}
